import type { PaymentMethod } from '@/types/checkout';
import type {
  AppchargePaymentElementCardRequest,
  AppchargePaymentElementCollectResult,
  AppchargePaymentElementFieldId,
  AppchargePaymentElementFieldState,
} from '@/types/appchargePaymentElement';
import type { useTranslate } from '@/hooks/useTranslate';
import { PaymentMethodEnum } from '@/types/common';
import type { ICheckoutContext } from '@/types/context';

type TranslateFn = ReturnType<typeof useTranslate>['t'];

export const getAppchargePaymentElementFieldErrorMessage = (
  field: AppchargePaymentElementFieldId,
  state: AppchargePaymentElementFieldState,
  t: TranslateFn
): string => {
  if (state.isValid) return '';

  switch (field) {
    case 'number':
      return state.isEmpty ? t('fieldErrors.incompleteCard') : t('fieldErrors.invalidCard');
    case 'expiry':
      return state.isEmpty ? t('fieldErrors.incompleteExpirationDate') : t('fieldErrors.invalidExpirationDate');
    case 'cvv':
      return state.isEmpty ? t('fieldErrors.incompleteSecurityCode') : t('fieldErrors.invalidSecurityCode');
    default:
      return '';
  }
};

export interface BuildAppchargePaymentElementCardRequestParams {
  cardCollectResult: AppchargePaymentElementCollectResult;
  paymentMethodSettings: PaymentMethod;
  email: string;
  cardholderName: string;
  shouldSaveCard: boolean;
}

export const buildAppchargePaymentElementCardRequest = ({
  cardCollectResult,
  paymentMethodSettings,
  email,
  cardholderName,
  shouldSaveCard,
}: BuildAppchargePaymentElementCardRequestParams): AppchargePaymentElementCardRequest => {
  if (!cardCollectResult.ok) {
    throw new Error(`Appcharge payment element collect failed: ${cardCollectResult.reason}`);
  }

  const { number, expiry, cvv } = cardCollectResult.data;

  return {
    paymentProvider: paymentMethodSettings.provider,
    receipt_email: email,
    params: { shouldSaveCard },
    paymentMethodDisplayName: paymentMethodSettings.paymentMethodDisplayName,
    paymentMethodId: paymentMethodSettings.id,
    card: {
      number: number.encryptedValue,
      expMonth: expiry.encryptedMonth ?? '',
      expYear: expiry.encryptedYear ? `20${expiry.encryptedYear}` : '',
      cvv: cvv.encryptedValue,
      cardholderName,
    },
  };
};

export const isAppchargeNewCardPayment = (checkout: ICheckoutContext, params: unknown): boolean =>
  checkout.paymentMethodSelected === PaymentMethodEnum.CREDIT_CARD &&
  checkout.checkoutConfiguration?.isRawPanSelfManaged === true &&
  (params as { newCard?: boolean } | undefined)?.newCard === true;
